import { useCallback, useEffect, useRef, useState } from 'react';

const useAiChatResponse = (data, handleChange, model, domain, generationType) => {
  const MAX_TOKENS = 29000;
  const CHARS_COUNT_PER_TOKEN = 4;

  const { responseList: initialResponseList, prompt: initialPrompt } = JSON.parse(data?.data);

  const [status, setStatus] = useState(null);
  const [streamState, setStreamState] = useState(null);
  const [responseList, setResponseList] = useState(initialResponseList);
  const [promptToRetry, setPromptToRetry] = useState(null);
  const [uniqueIdToRetry, setUniqueIdToRetry] = useState();
  const [prompt, setPrompt] = useState(initialPrompt);
  const [error, setError] = useState();

  const controllerRef = useRef(null);

  useEffect(() => {
    if (JSON.stringify(initialResponseList) !== JSON.stringify(responseList) || initialPrompt !== prompt) {
      handleChange({ target: { name: 'data', value: JSON.stringify({ responseList, prompt }) } });
    }
  }, [responseList, prompt, handleChange, initialResponseList, initialPrompt]);

  const handlePromptChange = useCallback(
    (newPrompt) => {
      setPrompt(newPrompt);

      const charCount = newPrompt.length;
      const tokenCount = charCount / CHARS_COUNT_PER_TOKEN;

      if (tokenCount > MAX_TOKENS) {
        setStatus('inputTooLong');
      }
    },
    [MAX_TOKENS]
  );

  const generateUniqueId = () => {
    const timestamp = Date.now();
    const randomNumber = Math.random();
    const hexadecimalString = randomNumber.toString(16);

    return `id-${timestamp}-${hexadecimalString}`;
  };

  const delay = (ms) => {
    return new Promise((resolve) => setTimeout(resolve, ms));
  };

  const addResponse = async (selfFlag, response) => {
    const uid = generateUniqueId();
    setResponseList((prevResponses) => [
      ...prevResponses,
      {
        id: uid,
        response,
        selfFlag,
      },
    ]);
    return uid;
  };

  const updateResponse = (uid, updatedObject) => {
    setResponseList((prevResponses) => {
      const updatedList = [...prevResponses];
      const index = prevResponses.findIndex((response) => response.id === uid);
      if (index > -1) {
        updatedList[index] = {
          ...updatedList[index],
          ...updatedObject,
        };
      }
      return updatedList;
    });
  };

  const updateResponseByChunk = (chunk) => {
    setResponseList((prevResponses) => {
      const updatedList = [...prevResponses];
      const index = prevResponses.length - 1;
      if (index > -1) {
        updatedList[index] = {
          ...updatedList[index],
          response: (updatedList[index].response || '') + chunk,
        };
      }
      return updatedList;
    });
  };

  const retryPrompt = async () => {
    await getPrompt(promptToRetry, uniqueIdToRetry);
  };

  let routeEndpoint = `/api/admin/accounts/${domain.account.id}/domains/${domain.id}/aiPrompts/agentNew`;

  const createTextRequestBody = (_prompt) => {
    const messagesWithLimitedLength = [
      ...responseList
        .filter((r) => r.response)
        .map((response) => {
          return {
            content: response.response,
            role: response.selfFlag ? 'user' : 'assistant',
          };
        }),
      { role: 'user', content: _prompt },
    ];

    if (messagesWithLimitedLength.length) {
      let countOfTokens = messagesWithLimitedLength.map((r) => r.content).join('').length / CHARS_COUNT_PER_TOKEN;

      while (countOfTokens > MAX_TOKENS) {
        const countOfTokensInMessage = messagesWithLimitedLength[0].content.length / CHARS_COUNT_PER_TOKEN;
        messagesWithLimitedLength.shift();
        countOfTokens -= countOfTokensInMessage;
      }
    }

    return {
      messages: messagesWithLimitedLength,
      modelParams: {
        top_p: model.advancedSettings.top_p,
        temperature: model.advancedSettings.temperature,
        system_prompt: model.advancedSettings.system_prompt,
      },
      contextSelection: { ...model.fastContextSelection },
      domainName: domain.name,
    };
  };

  const createImageRequestBody = (_prompt) => {
    const requestBody = {
      messages: [{ role: 'user', content: _prompt }],
      safeImageGeneration: model.advancedSettings.safeImageGeneration,
      modelParams: {
        seed: model.advancedSettings.seed,
        prompt_strength: model.advancedSettings.prompt_strength,
        aspect_ratio: model.advancedSettings.aspect_ratio,
        width: model.advancedSettings.width,
        height: model.advancedSettings.height,
      },
    };

    routeEndpoint += '/image';

    return requestBody;
  };

  const checkForState = (chunk) => {
    const stateRegex = /<state>(.*?)<\/state>/;
    const match = chunk.match(stateRegex);

    if (match) {
      const stateContent = match[1];
      return {
        isState: true,
        content: stateContent,
      };
    }

    return {
      isState: false,
      content: chunk,
    };
  };

  const getPrompt = async (_promptToRetry, _uniqueIdToRetry) => {
    // Get the prompt input
    let _prompt = _promptToRetry ?? prompt;

    // If a response is already being generated or the prompt is empty, return
    if (status === 'loading' || status === 'prompting' || !_prompt) {
      return;
    }

    setStatus('loading');
    setError(false);
    handlePromptChange('');

    let uniqueId;
    if (_uniqueIdToRetry) {
      uniqueId = _uniqueIdToRetry;
    } else {
      // Add the self prompt to the response list

      if (generationType === 'text') {
        await addResponse(true, _prompt);
        uniqueId = await addResponse(false);
        await delay(50);
      } else if (generationType === 'image') {
        await addResponse(true, _prompt);
        uniqueId = await addResponse(false);
        await delay(50);
      }
    }

    try {
      let requestBody;

      if (generationType === 'text') {
        requestBody = createTextRequestBody(_prompt);
      } else if (generationType === 'image') {
        requestBody = createImageRequestBody(_prompt);
      }

      controllerRef.current = new AbortController();
      const signal = controllerRef.current.signal;

      const response = await fetch(routeEndpoint, {
        method: 'POST',
        headers: {
          'content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
        signal,
      });

      setStatus('prompting');

      if (!response.ok) {
        setStatus('failed');
        const staticError = `Error: ${response.statusText}`;

        if (response.status === 504) {
          updateResponse(uniqueId, {
            response: 'Oops...The AI model is at capacity right now. Please try again later or use another model.',
            error: true,
          });

          return;
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');

        let result;
        let errorString = '';

        while (!result?.done) {
          result = await reader.read();
          let chunk = decoder.decode(result.value);
          errorString += chunk;
        }

        let finalError;
        try {
          const errorMessage = JSON.parse(errorString)?.message;
          finalError = errorMessage || staticError;
        } catch (err) {
          finalError = staticError;
        }

        updateResponse(uniqueId, {
          response: finalError,
          error: true,
        });

        return;
      }

      if (generationType === 'text') {
        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let result;
        while (!result?.done) {
          result = await reader.read();
          let chunk = decoder.decode(result.value);
          if (chunk) {
            const state = checkForState(chunk);
            if (state.isState) {
              setStreamState(state.content);
            } else {
              setStreamState(undefined);
              updateResponseByChunk(chunk);
            }
          }
        }
      } else if (generationType === 'image') {
        const { response: jsonResponse } = await response.json();
        // Show image for `Create image` model
        updateResponse(uniqueId, {
          image: jsonResponse,
        });
      }

      setStatus('completed');
    } catch (err) {
      // aborting the stream emits AbortError; not to be displayed in the UI
      if (err.name !== 'AbortError' && err !== 'User Aborted Streaming') {
        setPromptToRetry(_prompt);
        setUniqueIdToRetry(uniqueId);
        setStatus('failed');
        setError(`Error: ${err.message ? err.message : err.toString()}`);
      }
    } finally {
      if (status === 'completed') {
        setStatus(null);
      }
    }
  };

  const abortPrompt = () => {
    controllerRef.current.abort('User Aborted Streaming');
    setStatus('aborted');
  };

  return {
    responseList,
    status,
    error,
    prompt,
    streamState,
    handlePromptChange,
    getPrompt,
    retryPrompt,
    abortPrompt,
  };
};

export default useAiChatResponse;
